/* QuickLogModal — 5 BG predefined бързи храни. Логва с един клик и
 * опреснява Dashboard-а → empty-state веднага се заменя с реални OverviewCards.
 * Цел: секции до първи insight < 10 секунди (като Noom/MyFitnessPal early-habit loop).
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Coffee, Sandwich, Apple, Loader2, Croissant, Salad } from "lucide-react";

// Precomputed macros per typical BG portion. DIL via Nutramilo v2.0 algorithm
// (https://pypi.org/project/nutramilo-nis/).
// Values sourced from USDA + local equivalents — close enough for first-habit seed.
const QUICK_FOODS = [
  {
    id: "coffee_milk",
    icon: Coffee,
    color: "#8B4513",
    name_bg: "Кафе с мляко",
    name_en: "Coffee with milk",
    portion_bg: "1 чаша (240ml)",
    portion_en: "1 cup (240ml)",
    meal_type: "breakfast",
    amount_g: 240,
    calories: 85, protein: 5, carbs: 8, fat: 4, fiber: 0, sugar: 8, saturated_fat: 2,
  },
  {
    id: "banitsa",
    icon: Croissant,
    color: "#D4A574",
    name_bg: "Баница със сирене",
    name_en: "Cheese banitsa",
    portion_bg: "1 парче (120g)",
    portion_en: "1 slice (120g)",
    meal_type: "breakfast",
    amount_g: 120,
    calories: 332, protein: 10, carbs: 28, fat: 20, fiber: 1, sugar: 2, saturated_fat: 9,
  },
  {
    id: "yogurt_fruit",
    icon: Salad,
    color: "#D946EF",
    name_bg: "Йогурт с плодове",
    name_en: "Yogurt with fruit",
    portion_bg: "1 купа (200g)",
    portion_en: "1 bowl (200g)",
    meal_type: "snack",
    amount_g: 200,
    calories: 150, protein: 9, carbs: 20, fat: 4, fiber: 2, sugar: 18, saturated_fat: 2,
  },
  {
    id: "apple",
    icon: Apple,
    color: "#EF4444",
    name_bg: "Ябълка",
    name_en: "Apple",
    portion_bg: "1 средна (180g)",
    portion_en: "1 medium (180g)",
    meal_type: "snack",
    amount_g: 180,
    calories: 95, protein: 0.5, carbs: 25, fat: 0, fiber: 4, sugar: 19, saturated_fat: 0,
  },
  {
    id: "ham_sandwich",
    icon: Sandwich,
    color: "#F59E0B",
    name_bg: "Сандвич с шунка",
    name_en: "Ham sandwich",
    portion_bg: "1 броя (180g)",
    portion_en: "1 piece (180g)",
    meal_type: "lunch",
    amount_g: 180,
    calories: 330, protein: 18, carbs: 30, fat: 15, fiber: 2, sugar: 3, saturated_fat: 5,
  },
];

export default function QuickLogModal({ open, onOpenChange, onLogged }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [loadingId, setLoadingId] = useState(null);

  const handleQuickLog = async (food) => {
    setLoadingId(food.id);
    try {
      await axios.post(`${API}/meals/add`, {
        meal_type: food.meal_type,
        food_name: bg ? food.name_bg : food.name_en,
        amount_g: food.amount_g,
        calories: food.calories,
        protein: food.protein,
        carbs: food.carbs,
        fat: food.fat,
        fiber: food.fiber,
        sugar: food.sugar,
        saturated_fat: food.saturated_fat,
      });
      toast.success(bg ? `Записано: ${food.name_bg}` : `Logged: ${food.name_en}`);
      onLogged?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при запис" : "Error logging"));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-md ${isDark ? "bg-card" : ""}`} data-testid="quick-log-modal">
        <DialogHeader>
          <DialogTitle>{bg ? "Запиши за 10 секунди" : "Log in 10 seconds"}</DialogTitle>
          <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg ? "Избери бързо хранене — ще се попълнят стойностите веднага." : "Pick a quick food — values populate instantly."}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-2 mt-2">
          {QUICK_FOODS.map((food) => {
            const Icon = food.icon;
            const loading = loadingId === food.id;
            return (
              <button
                key={food.id}
                onClick={() => handleQuickLog(food)}
                disabled={loadingId !== null}
                data-testid={`quicklog-${food.id}`}
                className={`flex items-center gap-3 p-3 rounded-xl text-left transition-all ${isDark ? "bg-[hsl(223,47%,12%)] hover:bg-[hsl(223,47%,15%)] border border-[hsl(223,47%,20%)]" : "bg-white hover:bg-gray-50 border border-gray-100 shadow-sm"} disabled:opacity-50`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${food.color}15` }}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: food.color }} /> : <Icon className="w-5 h-5" style={{ color: food.color }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                    {bg ? food.name_bg : food.name_en}
                  </p>
                  <p className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {bg ? food.portion_bg : food.portion_en} · {food.calories} kcal · {food.protein}P/{food.carbs}C/{food.fat}F
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <p className={`text-[10px] text-center mt-2 ${isDark ? "text-gray-600" : "text-gray-400"}`}>
          {bg ? "За персонализирано търсене използвайте Хранителния дневник по-долу." : "For custom search, use the Meal Log below."}
        </p>
      </DialogContent>
    </Dialog>
  );
}
